import React from "react";
import { motion } from "framer-motion";
import { useReducedMotionSafe } from "@/components/animation/AnimationSettings";
import { PALETTE } from "@/components/animation/MotionShared";

/**
 * Animated confidence pulse — a small radial / linear indicator with a soft
 * pulsing aura, anchored to a 0–100 confidence value. Premium gold accent.
 */
export default function ConfidencePulse({
  value = 0,
  label = "CONFIDENCE",
  testid = "confidence-pulse",
  size = 64,
}) {
  const reduce = useReducedMotionSafe();
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const sizeN = (() => {
    const n = Number(size);
    return Number.isFinite(n) && n > 0 ? n : 64;
  })();
  const ringStroke = 6;
  const radius = Math.max(1, (sizeN - ringStroke * 1.6) / 2);
  const c = 2 * Math.PI * radius;
  const fill = c * (v / 100);
  const color = v >= 75 ? "#10b981" : v >= 50 ? "#22d3ee" : v >= 30 ? "#fcd34d" : "#fb923c";
  return (
    <div style={S.shell} data-testid={testid}>
      <div style={{ position: "relative", width: sizeN, height: sizeN }}>
        {!reduce && (
          <motion.span
            aria-hidden="true"
            style={{ position: "absolute", inset: 0, borderRadius: "50%", boxShadow: `0 0 22px ${color}aa`, background: `radial-gradient(circle, ${color}33 0%, transparent 70%)` }}
            animate={{ scale: [1, 1.12, 1], opacity: [0.55, 0.95, 0.55] }}
            transition={{ duration: 2.1, repeat: Infinity, ease: "easeInOut" }}
          />
        )}
        <svg width={sizeN} height={sizeN} viewBox={`0 0 ${sizeN} ${sizeN}`}>
          <circle cx={sizeN / 2} cy={sizeN / 2} r={radius} stroke="rgba(255,255,255,0.08)" strokeWidth={ringStroke} fill="none" />
          <motion.circle
            cx={sizeN / 2} cy={sizeN / 2} r={radius}
            stroke={color} strokeLinecap="round" strokeWidth={ringStroke} fill="none"
            transform={`rotate(-90 ${sizeN / 2} ${sizeN / 2})`}
            strokeDasharray={c}
            initial={{ strokeDashoffset: c }}
            animate={{ strokeDashoffset: c - fill }}
            transition={reduce ? { duration: 0 } : { duration: 1, ease: "easeOut" }}
          />
          <text x="50%" y="54%" textAnchor="middle" dominantBaseline="middle"
            style={{ fill: PALETTE.goldBright, fontFamily: "'Space Mono', monospace", fontWeight: 800, fontSize: 16 }}>
            {Math.round(v)}
          </text>
        </svg>
      </div>
      <span style={S.label}>{label}</span>
    </div>
  );
}

const S = {
  shell: { display: "flex", flexDirection: "column", alignItems: "center", gap: 4 },
  label: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: "0.18em" },
};
